import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import apiFetch, { safeJson } from "../../../context/api";

/**
 * Pantalla de Barra (CRUO F1) — cola COMPARTIDA de comandas.
 *
 * Es "solo una URL": cualquier tablet/móvil del staff la abre. Las dos barras ven
 * TODAS las comandas; quien coge una la marca "preparando" y queda suya (claim en
 * backend). Polling corto (sin websockets, ARQUITECTURA_CARRITO_V2.md §2.3) +
 * aviso sonoro cuando entra comanda nueva.
 */

const POLL_MS = 5000;

const ESTADOS = {
  pending:   { label: "Pendiente",  next: "preparing", accion: "Preparar",  tone: "bg-amber-100 text-amber-800 border-amber-300" },
  preparing: { label: "Preparando", next: "completed", accion: "Servida ✓", tone: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  completed: { label: "Completada", next: null,        accion: null,        tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Cancelada",  next: null,        accion: null,        tone: "bg-rose-100 text-rose-700 border-rose-300" },
};

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    /* sin audio no pasa nada */
  }
}

function edadMinutos(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return mins < 1 ? "ahora" : `hace ${mins} min`;
}

function ComandaCard({ order, onAdvance, onCancel, busy }) {
  const meta = ESTADOS[order.status] || ESTADOS.pending;
  const esServe = order.fulfillment === "serve";
  return (
    <div className={`rounded-xl border-2 p-4 bg-white shadow-sm ${order.status === "pending" ? "border-amber-300" : "border-gray-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-gray-900">
            {order.customer_name || "Socio"}
            <span className="ml-2 text-xs font-normal text-gray-400">#{String(order.id).slice(0, 8)}</span>
          </div>
          <div className="mt-0.5 text-sm">
            {esServe ? (
              <span className="font-medium text-violet-700">🍽️ Servir en mesa</span>
            ) : (
              <span className="font-medium text-emerald-700">🛎️ Recoge en barra</span>
            )}
            <span className="ml-2 text-xs text-gray-400">{edadMinutos(order.created_at)}</span>
          </div>
        </div>
        <span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.tone}`}>
          {meta.label}
        </span>
      </div>

      <ul className="mt-3 space-y-1 border-t border-gray-100 pt-2">
        {(order.items || []).map((it) => (
          <li key={it.id} className="flex justify-between text-sm">
            <span>
              <b className="text-gray-900">{it.quantity}×</b> {it.product_name}
              {it.notes ? <span className="ml-1 text-xs italic text-gray-500">({it.notes})</span> : null}
            </span>
            <span className="tabular-nums text-gray-500">{Number(it.subtotal).toFixed(2)} €</span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
        <span className="text-sm font-bold text-gray-900">{Number(order.total).toFixed(2)} €</span>
        <div className="flex gap-2">
          {order.status === "pending" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onCancel(order)}
              className="rounded-lg px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-40"
            >
              Cancelar
            </button>
          )}
          {meta.next && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAdvance(order, meta.next)}
              className="rounded-lg bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-40"
            >
              {meta.accion}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BarraPage() {
  const [token, , , venueID] = useContext(UserContext);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [verCompletadas, setVerCompletadas] = useState(false);
  const conocidasRef = useRef(new Set()); // ids ya vistos → beep solo con las nuevas

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "X-Venue-Id": venueID || "" }),
    [token, venueID]
  );

  const cargar = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await apiFetch(`/v1/orders?limit=100`, { headers: authHeaders });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail;
        throw new Error(typeof detail === "string" ? detail : "No se pudieron cargar las comandas");
      }
      const data = await safeJson(res);
      // Beep solo si aparece una comanda pendiente que no habíamos visto.
      const nuevasPendientes = (data || []).filter(
        (o) => o.status === "pending" && !conocidasRef.current.has(o.id)
      );
      if (conocidasRef.current.size > 0 && nuevasPendientes.length > 0) beep();
      for (const o of data || []) conocidasRef.current.add(o.id);
      setOrders(data || []);
      setError("");
    } catch (e) {
      setError(e.message || "Error cargando comandas");
    }
  }, [token, venueID, authHeaders]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, POLL_MS);
    return () => clearInterval(t);
  }, [cargar]);

  const cambiarEstado = useCallback(
    async (order, nuevo) => {
      setBusyId(order.id);
      try {
        const res = await apiFetch(`/v1/orders/${order.id}/status`, {
          method: "PATCH",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ status: nuevo }),
        });
        if (!res.ok) throw new Error("No se pudo actualizar la comanda");
        await cargar();
      } catch (e) {
        setError(e.message);
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, cargar]
  );

  const activas = orders.filter((o) => o.status === "pending" || o.status === "preparing");
  const cerradas = orders.filter((o) => o.status === "completed" || o.status === "cancelled");
  const pendientes = activas.filter((o) => o.status === "pending").length;

  return (
    <div className="flex max-w-full flex-col gap-4 overflow-x-hidden p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Barra · Comandas</h1>
          <p className="text-sm text-gray-500">
            Cola compartida — quien coge una comanda, la reclama. Se actualiza sola.
          </p>
        </div>
        <div className="text-right">
          <div
            data-testid="pendientes-count"
            className={`text-3xl font-extrabold ${pendientes > 0 ? "text-amber-600" : "text-emerald-600"}`}
          >
            {pendientes}
          </div>
          <div className="text-xs uppercase tracking-wide text-gray-400">pendientes</div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {activas.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-400">
          No hay comandas activas. ☕
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {activas.map((o) => (
          <ComandaCard
            key={o.id}
            order={o}
            busy={busyId === o.id}
            onAdvance={cambiarEstado}
            onCancel={(ord) => cambiarEstado(ord, "cancelled")}
          />
        ))}
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => setVerCompletadas((v) => !v)}
          className="text-sm text-gray-500 underline-offset-2 hover:underline"
        >
          {verCompletadas ? "Ocultar" : "Ver"} completadas de hoy ({cerradas.length})
        </button>
        {verCompletadas && (
          <div className="mt-3 grid grid-cols-1 gap-3 opacity-70 md:grid-cols-2 xl:grid-cols-3">
            {cerradas.map((o) => (
              <ComandaCard key={o.id} order={o} busy onAdvance={() => {}} onCancel={() => {}} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
